import { Endpoint, Page } from '~src/domains/common/enums/api';
import { PaymentOption } from '~src/domains/common/enums/common';
import { StateObject } from '~src/domains/common/types/common';
import { LoanOfferType } from '~src/domains/common/types/offers';
import { DISABLE_CEVENTS } from './constants';

const { version } = require('../../package.json');
const NODE_ENV = process.env.NODE_ENV;

enum EventLabel {
  DID_MOUNT = 'did-mount',
  ALERT = 'alert',
  INVALID = 'invalid',
  BIN = 'bin',
  ORDER_CREATE = 'order-create',
  PAYMENT = 'payment',
  UPLIFT_CHECKOUT = 'Uplift.Checkout',
  LOAD = 'load',
  PLAID_LINK_TOKEN = 'plaid-link-token-generation',
}

enum EventCategory {
  SYS = 'sys',
  USER = 'user',
}

enum EventAction {
  HANDLER = 'handler',
  VIEW = 'view',
  BLUR = 'blur',
  FOCUS = 'focus',
  INIT = 'init',
  MOUSE_UP = 'mouseup',
  POST = 'post',
  POST_RESULT = 'post-result',
  TRIP_CHANGED = 'trip-changed',
  AUTH = 'auth',
  MOUSE_OUT = 'mouseout',
  RAW = 'raw',
  OFFER_EXPIRED = 'offer-expired',
  POLL_FAIL = 'poll-fail',
  POLL_TIMEOUT = 'poll-timeout',
  SELECT = 'select',
  GET = 'get',
}

// Gets client ID from cookies for cevents
const getCid = () => {
  const cookies = document.cookie;

  if (!cookies.length) {
    return;
  }

  const cookiesArray = cookies.split('; ');

  for (const cookie of cookiesArray) {
    const [name, value] = cookie.split('=');

    // e.g. "1.2.1887949290.1646940325" => "1887949290.1646940325"
    if (name === '_up') {
      return value.split('.')?.slice(2)?.join('.') || null;
    }
  }

  return null;
};

const sendCevent =
  (
    eventCategory: EventCategory,
    eventAction: EventAction,
    eventPayload: Object = {},
    eventElement: string = '',
  ) =>
  async (_: Function, getState: Function) => {
    try {
      const state: StateObject = getState();
      const payload: any = {
        ...eventPayload,
        'offer-id': state.common.offerId,
        version,
      };

      const filteredPayload = Object.keys(payload)
        .filter(key => payload[key] != null)
        .reduce(
          (prev: any, key: any) => ({ ...prev, [key]: payload[key] }),
          {},
        );

      const cid = getCid();

      if (NODE_ENV === 'test') {
        return;
      } else if (DISABLE_CEVENTS === 'true') {
        console.log(
          'CEVENT',
          cid,
          eventCategory,
          eventAction,
          eventElement,
          filteredPayload,
        );
      } else {
        (window as any).up('send', 'client', {
          cid,
          ec: eventCategory,
          ea: eventAction,
          el: eventElement,
          ev: filteredPayload,
          tid: state.common.upcode,
        });
      }
    } catch (e) {
      console.error('cevent error:', e);
    }
  };

export const trackAuth =
  (paymentMethod: PaymentOption) => (dispatch: Function) => {
    dispatch(
      sendCevent(EventCategory.SYS, EventAction.AUTH, {
        payment_method: paymentMethod,
      }),
    );
  };

export const trackFocus = (path: string[]) => (dispatch: Function) => {
  dispatch(
    sendCevent(
      EventCategory.SYS,
      EventAction.FOCUS,
      { path },
      EventLabel.INVALID,
    ),
  );
};

export const trackHandler =
  (handler: string, payload: Object = {}) =>
  (dispatch: Function) => {
    dispatch(
      sendCevent(EventCategory.SYS, EventAction.HANDLER, payload, handler),
    );
  };

export const trackPageMount =
  (page: string) => (dispatch: Function, getState: Function) => {
    const state = getState();
    const paymentMethod = state.common.paymentMethod;
    const loanOffers = state.common.session?.loan_offers;
    const optionalEventValue: {
      'payment-method'?: string;
      options?: string[];
    } = {};
    const trackLoanOffers =
      loanOffers?.length &&
      (page === Page.ACCEPT ||
        page === Page.REACCEPT ||
        page === Page.RECONFIRM);

    if (paymentMethod) {
      optionalEventValue['payment-method'] = paymentMethod;
    }

    // We only track loan offer options if on Accept or Reaccept page
    optionalEventValue.options = trackLoanOffers
      ? loanOffers.map((offer: LoanOfferType) => {
          const offerCopy = Object.assign({}, offer);

          offerCopy.offer = offer['offer?'];

          delete offerCopy.docs;
          delete offerCopy.localization;
          delete offerCopy['offer?'];

          return offerCopy;
        })
      : null;

    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.VIEW,
        { ...optionalEventValue, page },
        EventLabel.DID_MOUNT,
      ),
    );
  };

export const trackCheckoutInit = () => (dispatch: Function) => {
  dispatch(
    sendCevent(
      EventCategory.SYS,
      EventAction.INIT,
      {},
      EventLabel.UPLIFT_CHECKOUT,
    ),
  );
};

export const trackSelectPayment = (value: string) => (dispatch: Function) => {
  dispatch(
    sendCevent(
      EventCategory.USER,
      EventAction.SELECT,
      {
        value,
      },
      EventLabel.PAYMENT,
    ),
  );
};

export const trackOrderLoad = () => (dispatch: Function) => {
  dispatch(
    sendCevent(
      EventCategory.SYS,
      EventAction.GET,
      {
        origin: window.location.origin,
      },
      EventLabel.LOAD,
    ),
  );
};

export const trackFormBlur = (elementId: string) => (dispatch: Function) => {
  dispatch(sendCevent(EventCategory.USER, EventAction.BLUR, {}, elementId));
};

export const trackClick =
  (label: string, payload: Object = {}) =>
  (dispatch: Function) => {
    dispatch(
      sendCevent(EventCategory.USER, EventAction.MOUSE_UP, payload, label),
    );
  };

export const trackMouseOut =
  (label: string, payload: Object = {}) =>
  (dispatch: Function) => {
    dispatch(
      sendCevent(EventCategory.USER, EventAction.MOUSE_OUT, payload, label),
    );
  };

export const trackXhr = () => (dispatch: Function, getState: Function) => {
  const state = getState();

  dispatch(
    sendCevent(
      EventCategory.SYS,
      EventAction.POST,
      {},
      state.common.next?.toString(),
    ),
  );
};

export const trackXhrResult =
  (
    prevStep: Endpoint,
    status: number,
    mcode: number | string,
    message: string,
  ) =>
  (dispatch: Function) => {
    const payload = {
      message,
      status,
      mcode,
    };

    dispatch(
      sendCevent(EventCategory.SYS, EventAction.POST_RESULT, payload, prevStep),
    );
  };

export const trackRaw =
  (type: string, value: number) => (dispatch: Function) => {
    dispatch(sendCevent(EventCategory.SYS, EventAction.RAW, { value }, type));
  };

export const trackBinXhrResult =
  (status: string, valid: boolean, mcode: number) => (dispatch: Function) => {
    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.POST_RESULT,
        {
          message: EventLabel.BIN,
          status,
          valid,
          mcode,
        },
        EventLabel.BIN,
      ),
    );
  };

export const trackOrderCreateXhr = () => (dispatch: Function) => {
  dispatch(
    sendCevent(
      EventCategory.SYS,
      EventAction.POST,
      {},
      EventLabel.ORDER_CREATE,
    ),
  );
};

export const trackOrderCreateResultXhr =
  (status: string | number, mcode?: number) => (dispatch: Function) => {
    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.POST_RESULT,
        {
          mcode: mcode || null,
          status: status.toString(),
        },
        EventLabel.ORDER_CREATE,
      ),
    );
  };

export const trackBinXhr = () => (dispatch: Function) => {
  dispatch(sendCevent(EventCategory.SYS, EventAction.POST, {}, EventLabel.BIN));
};

export const trackAlert =
  (alert: string, mcode: number) => (dispatch: Function) => {
    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.VIEW,
        { alert, status: mcode },
        EventLabel.ALERT,
      ),
    );
  };

export const trackDocumentRequest = (docId: string) => (dispatch: Function) => {
  dispatch(sendCevent(EventCategory.SYS, EventAction.POST, {}, docId));
};

export const trackDocumentResponse =
  (docId: string, success: boolean) => (dispatch: Function) => {
    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.POST_RESULT,
        { success },
        docId,
      ),
    );
  };

export const trackTripChanged =
  () => (dispatch: Function, getState: Function) => {
    const state = getState();

    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.TRIP_CHANGED,
        {},
        state.common.page,
      ),
    );
  };

export const trackOfferExpired =
  () => (dispatch: Function, getState: Function) => {
    const state = getState();

    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.OFFER_EXPIRED,
        {},
        state.common.page,
      ),
    );
  };

export const trackXhrPollTimeout =
  () => (dispatch: Function, getState: Function) => {
    const state = getState();

    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.POLL_TIMEOUT,
        {},
        state.common.page,
      ),
    );
  };

export const trackXhrPollFail =
  () => (dispatch: Function, getState: Function) => {
    const state = getState();

    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.POLL_FAIL,
        {},
        state.common.page,
      ),
    );
  };

export const trackXhrLinkToken =
  (response?: any, status?: number) => (dispatch: Function) => {
    if (!response || !status) {
      dispatch(
        sendCevent(
          EventCategory.SYS,
          EventAction.POST,
          {},
          EventLabel.PLAID_LINK_TOKEN,
        ),
      );

      return;
    }

    const isError = typeof response === 'string';
    let payload: any = {
      status,
    };

    if (isError) {
      payload.error = response;
    } else {
      payload = {
        ...payload,
        ...response,
      };
    }

    dispatch(
      sendCevent(
        EventCategory.SYS,
        EventAction.POST_RESULT,
        payload,
        EventLabel.PLAID_LINK_TOKEN,
      ),
    );
  };
